import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { decodeHtmlEntities } from '../../../containers/utils';
import LeadLockOverlay from "./lead-lock-overlay";
import MagicPopup from "../../MagicPopup";
import RequestLeadAccess from "./request-lead-access";

const currencySymbols = {
  USD: '$',
  GBP: '£',
  EUR: '€',
};

const OpportunityCard = (props) => {
  const locked = props.lead.locked === true;

  const [showBubble, setShowBubble] = React.useState(false);
  const [showLockPopup, setShowLockPopup] = React.useState(false);

  const handleMouseEnter = () => {
    if (!locked) {
      setShowBubble(true);
    }
  };

  const handleMouseLeave = () => {
    setShowBubble(false);
  };

  const handleLockPopupResolve = () => {
    setShowLockPopup(false);
  };

  const isSoldOut = props.lead.places === 0;

  const [isHidden, setIsHidden] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const hideButtonRef = useRef(null);

  useEffect(() => {
    const isLeadHidden = localStorage.getItem(`hidden_lead_${props.lead.id}`);
    setIsHidden(isLeadHidden === 'true');
  }, [props.lead.id]);

  const handleHideClick = () => {
    setShowModal(true);
  };

  const handleHideLead = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsHidden(true);
      setShowModal(false);
      setIsAnimating(false);
      localStorage.setItem(`hidden_lead_${props.lead.id}`, 'true');
    }, 500);
  };

  const handleCancelHide = () => {
    setShowModal(false);
  };

  const handleLeadClick = () => {
    if (!locked) {
      props.onClick(props.lead.id);
      return;
    }

    setShowLockPopup(true);
  };

  if (isHidden) {
    return null;
  }

  const calculateModalYPosition = () => {
    if (hideButtonRef.current) {
      const hideButtonRect = hideButtonRef.current.getBoundingClientRect();
      const hideButtonTop = hideButtonRect.top + window.scrollY;
      return hideButtonTop - 30;
    } else {
      return null;
    }
  };

  const modalYPosition = calculateModalYPosition();

  let bubbleText = '';

  if (props.lead.places === 2) {
    bubbleText = 'Low availability';
  } else if (props.lead.places >= 5) {
    bubbleText = 'Low competition';
  } else if (props.lead.places === 1) {
    bubbleText = 'One send left';
  } else if (props.lead.places === 0) {
    bubbleText = 'Sold Out';
  } else {
    bubbleText = '';
  }

  let displayFields;

  if (props.lead.market_name === 'EHR') {
    displayFields = {
      firstField: {
        name: 'Practitioners',
        value: decodeHtmlEntities(props.lead.practicesizenonrange) || 'N/A',
      },
      secondField: {
        name: 'Specialty',
        value: decodeHtmlEntities(props.lead.practicespecialty) || 'N/A',
      }
    };
  } else if (props.lead.market_name === 'HRMS') {
    displayFields = {
      firstField: {
        name: 'Revenue',
        value: decodeHtmlEntities(props.lead.revenue_formatted) || 'N/A',
        aria_label: props.lead.revenue_formatted
            ? decodeHtmlEntities(props.lead.revenue_formatted)
            .replace('$', '')
            .replace('£', '')
            .replace('bn', ' billion')
            .replace('m', ' million') + ' dollars'
            : '',
      },
      secondField: {
        name: 'Industry',
        value: decodeHtmlEntities(props.lead.sagecampaignindustries) || 'N/A',
      }
    };
  } else {
    displayFields = {
      firstField: {
        name: 'Revenue',
        value: props.lead.revenue_formatted || 'N/A',
        aria_label: props.lead.revenue_formatted
            ? props.lead.revenue_formatted
            .replace('$', '')
            .replace('bn', ' billion')
            .replace('m', ' million') + ' dollars'
            : '',
      },
      secondField: {
        name: 'Industry',
        value: decodeHtmlEntities(props.lead.industry_sector) || 'N/A',
      },
    };
  }

  const rootStyle = {
    position: 'relative',
  };

  if (isSoldOut) {
    rootStyle.backgroundColor = '#FAEBEB';
  }

  return (
    <React.Fragment>
      <div
        className={`data-list-tr ${isAnimating ? 'slide-up' : ''}`}
        style={rootStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="data-list-td data-list-main-col tab-lead-alignment" onClick={handleLeadClick}>
          <div className="data-list-tab">
            <div className={`tab ${props.lead.qualification_type === 'phone_qualified' ? 'tab-tq' : 'tab-mql'}`}>
              <span>
                {props.lead.qualification_type === 'phone_qualified' ? 'TQ' : 'MQL+'}
              </span>
            </div>
          </div>
          {bubbleText && (
            <div className={`info-bubble ${showBubble && !isSoldOut ? 'active' : ''} ${isSoldOut ? 'sold-out' : ''}`}>
              <span className={`info-icon ${isSoldOut ? 'sold-out' : ''}`}>i</span>
              <span className={`info-text ${isSoldOut ? 'sold-out' : ''}`}>{bubbleText}</span>
            </div>
          )}
          <em className="t-bold">{$('<textarea />').html(props.lead.headline).text()}</em>
        </div>
        <div className="data-list-td data-list-td--center data-list-5-col data-first-row">
          <div className="data-list-td-header">{displayFields.firstField.name}</div>
          {displayFields.firstField.value}
        </div>
        <div className="data-list-td data-list-td--center data-list-5-col align-row">
          <div className="data-list-td-header">{displayFields.secondField.name}</div>
          {displayFields.secondField.value}
        </div>
        <div className="data-list-td data-list-td--center data-list-5-col align-row">
          <div className="data-list-td-header">Core Problem</div>
          {decodeHtmlEntities(props.lead.problem) || 'N/A'}
        </div>
        <div className="data-list-td data-list-td--center data-list-5-col align-row">
          <div className="data-list-td-header">Received</div>
          {
            props.lead.combined_date ?
                moment(props.lead.combined_date).format('MM/DD') :
                ''
          }
        </div>
        <div className="data-list-td data-list-td--center data-list-5-col align-row">
          <div className="data-list-td-header">Price</div>
          <span className="flair--price" style={{ zIndex: 1, backgroundColor: '#d1ebf1' }}>
            {props.lead.price
                ? `${currencySymbols[props.lead.currency] || ''}${props.lead.price}`
                : 'N/A'}
          </span>
        </div>

        <div className="data-list-td data-list-td--center" style={{display: 'flex', alignItems: 'center'}}>
          <span className="red-circle-with-x" onClick={handleHideClick} ref={hideButtonRef}>
            X
          </span>
        </div>
        {locked && (
          <LeadLockOverlay onClick={handleLeadClick} />
        )}
      </div>

      {showModal && (
        <div className="modal" style={{position: 'absolute', right: 140, top: modalYPosition}}>
          <div className="modal-content">
            <p>Do you want to hide this lead?</p>
            <div>
              <button className="yes" onClick={handleHideLead}>YES</button>
              <button className="no" onClick={handleCancelHide}>NO</button>
            </div>
          </div>
        </div>
      )}

      {showLockPopup && (
        <MagicPopup title="Unlock Lead" resolveButtonText="Close" onResolve={handleLockPopupResolve}>
          <RequestLeadAccess leadId={props.lead.id} />
        </MagicPopup>
      )}
    </React.Fragment>
  );
};

OpportunityCard.propTypes = {
  lead: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  currency: PropTypes.string.isRequired,
};

OpportunityCard.defaultProps = {
  onClick: () => {},
};

export default OpportunityCard;
